import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { addOpacityToHexColor } from 'commons/utils/addOpacityToHexColor';
import { useTranslationStatistics } from 'domains/Statistics/hooks';
import { StatisticsGranularityType } from 'domains/Statistics/types';
import {
  abbreviateNumber,
  BAR_CHART_HEIGTH,
  CHART_COLORS,
  FONT_SIZE_BAR_CHART,
  MARGIN_BAR_CHART,
} from 'domains/Statistics/utils';
import xAxisFormatDate from 'domains/Statistics/utils/xAxisFormatDate';
import LegendStratus from '../../LegendStratus';
import StratusLegendContent from '../../StratusLegendContent';
import StratusTooltipContent from '../../StratusTooltipContent';
import TooltipChartStratus from '../../TooltipChartStratus';

interface SalesChannelsRevenueBarChartValues {
  name: string;
  secondaryName?: string;
  mobile: number;
  api: number;
  store: number;
  form: number;
  'not-provided': number;
  mobileCompared?: number;
  apiCompared?: number;
  storeCompared?: number;
  formCompared?: number;
  'not-providedCompared'?: number;
}

interface SalesChannelsRevenueBarChartProps {
  values: SalesChannelsRevenueBarChartValues[];
  hasComparedFilter: boolean;
  granularity: StatisticsGranularityType;
}

function SalesChannelsRevenueBarChart({
  values,
  hasComparedFilter,
  granularity,
}: SalesChannelsRevenueBarChartProps) {
  const t = useTranslationStatistics();
  const handleFormatedDate = xAxisFormatDate(granularity);

  const formatLabel = (label: string) => {
    const cleanLabel = label.replace('Compared', '');
    return t(`statistics.salesChannelsRevenueCard.${cleanLabel}`);
  };

  return (
    <ResponsiveContainer height={BAR_CHART_HEIGTH}>
      <BarChart
        data={values}
        margin={{ left: -MARGIN_BAR_CHART, right: MARGIN_BAR_CHART }}
        style={{ fontSize: FONT_SIZE_BAR_CHART }}
        maxBarSize={values.length > 8 ? 12 : undefined}
      >
        <CartesianGrid vertical={false} strokeDasharray="2 0" />
        <XAxis dataKey="name" tickFormatter={handleFormatedDate} />
        <YAxis
          tickFormatter={abbreviateNumber}
          axisLine={false}
          tickLine={false}
        />
        <Legend
          content={
            <LegendStratus>
              <StratusLegendContent
                hasComparedData={hasComparedFilter}
                formatLabel={formatLabel}
                isCombined
              />
            </LegendStratus>
          }
        />
        <Tooltip
          content={
            <TooltipChartStratus>
              <StratusTooltipContent
                granularity={granularity}
                hasComparedData={hasComparedFilter}
                formatLabel={formatLabel}
                isCombined
              />
            </TooltipChartStratus>
          }
        />
        <Bar
          dataKey="mobile"
          stackId="principal"
          fill={CHART_COLORS.skyBlue}
          stroke={CHART_COLORS.skyBlue}
        />
        <Bar
          dataKey="api"
          stackId="principal"
          fill={addOpacityToHexColor(CHART_COLORS.skyBlue, 0.8)}
          stroke={CHART_COLORS.skyBlue}
        />
        <Bar
          dataKey="store"
          stackId="principal"
          fill={addOpacityToHexColor(CHART_COLORS.skyBlue, 0.6)}
          stroke={CHART_COLORS.skyBlue}
        />
        <Bar
          dataKey="form"
          stackId="principal"
          fill={addOpacityToHexColor(CHART_COLORS.skyBlue, 0.4)}
          stroke={CHART_COLORS.skyBlue}
        />
        {/* <Bar
          dataKey="not-provided"
          stackId="principal"
          fill={addOpacityToHexColor(CHART_COLORS.skyBlue, 0.2)}
          stroke={CHART_COLORS.skyBlue}
        /> */}

        {hasComparedFilter && (
          <>
            <Bar
              dataKey="mobileCompared"
              stackId="compared"
              fill={CHART_COLORS.navyBlue}
              stroke={CHART_COLORS.navyBlue}
            />
            <Bar
              dataKey="apiCompared"
              stackId="compared"
              fill={addOpacityToHexColor(CHART_COLORS.navyBlue, 0.8)}
              stroke={CHART_COLORS.navyBlue}
            />
            <Bar
              dataKey="storeCompared"
              stackId="compared"
              fill={addOpacityToHexColor(CHART_COLORS.navyBlue, 0.6)}
              stroke={CHART_COLORS.navyBlue}
            />
            <Bar
              dataKey="formCompared"
              stackId="compared"
              fill={addOpacityToHexColor(CHART_COLORS.navyBlue, 0.4)}
              stroke={CHART_COLORS.navyBlue}
            />
            {/* <Bar
              dataKey="not-providedCompared"
              stackId="compared"
              fill={addOpacityToHexColor(CHART_COLORS.navyBlue, 0.2)}
              stroke={CHART_COLORS.navyBlue}
            /> */}
          </>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default SalesChannelsRevenueBarChart;
